// src/state/storage.ts
// localStorage persistence (spec §7). Everything namespaced `colorslide:v1:*`.
// Hard rules: safe no-op defaults when keys are absent; NEVER throw on bad/missing storage
// (private-mode, quota, corrupt JSON, disabled storage all degrade gracefully). No network.

// ---- Settings (spec §5.6) --------------------------------------------------

/** Reduced-motion preference: 'auto' follows the OS media query; on/off force it. */
export type ReducedMotionPref = 'auto' | 'on' | 'off';

export interface Settings {
  /** Sound effects (synthesized WebAudio; §5.4). Honored by the Sfx engine. */
  readonly sound: boolean;
  /** Per-color symbols drawn on blocks/gates (§5.3). On by default. */
  readonly symbols: boolean;
  /** Reduced motion (§5.4). 'auto' defers to prefers-reduced-motion. */
  readonly reducedMotion: ReducedMotionPref;
}

export const DEFAULT_SETTINGS: Settings = {
  sound: true,
  symbols: true,
  reducedMotion: 'auto',
};

/** Cosmetic per-level record (§2.6, §7). `stars` is 0 or 1 in v1 (at/under par). */
export interface LevelRecord {
  readonly completed: boolean;
  readonly stars: number;
}

/** Map keyed by the stringified 1-based level index. */
export type ProgressMap = Readonly<Record<string, LevelRecord>>;

// ---- Namespacing -----------------------------------------------------------

const NS = 'colorslide:v1:';
const KEY_CURRENT_LEVEL = `${NS}currentLevel`;
const KEY_SETTINGS = `${NS}settings`;
const KEY_PROGRESS = `${NS}progress`;
const KEY_TUTORIAL_SEEN = `${NS}tutorialSeen`;

/** Returns the Storage object, or null when unavailable/blocked (never throws). */
function safeStorage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    if (s === undefined || s === null) {
      return null;
    }
    // Probe: some environments expose localStorage but throw on access.
    const probe = `${NS}__probe__`;
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

function readRaw(key: string): string | null {
  try {
    const s = safeStorage();
    return s === null ? null : s.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    const s = safeStorage();
    if (s !== null) {
      s.setItem(key, value);
    }
  } catch {
    // Quota or serialization failure: ignore. Persistence is best-effort.
  }
}

function removeRaw(key: string): void {
  try {
    const s = safeStorage();
    if (s !== null) {
      s.removeItem(key);
    }
  } catch {
    // ignore
  }
}

// ---- currentLevel (resume, §7) ---------------------------------------------

/** Resume target on launch. Defaults to 1 when absent/invalid. Always returns an integer >= 1. */
export function loadCurrentLevel(): number {
  const raw = readRaw(KEY_CURRENT_LEVEL);
  if (raw === null) {
    return 1;
  }
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) {
    return 1;
  }
  return n;
}

export function saveCurrentLevel(level: number): void {
  const n = Number.isFinite(level) && level >= 1 ? Math.floor(level) : 1;
  writeRaw(KEY_CURRENT_LEVEL, String(n));
}

// ---- tutorialSeen (first-run gate, §5.5) -----------------------------------

/** True once the first-run visual tutorial has been shown. Defaults to false. Never throws. */
export function loadTutorialSeen(): boolean {
  return readRaw(KEY_TUTORIAL_SEEN) === '1';
}

export function saveTutorialSeen(seen: boolean): void {
  if (seen) {
    writeRaw(KEY_TUTORIAL_SEEN, '1');
  } else {
    removeRaw(KEY_TUTORIAL_SEEN);
  }
}

// ---- settings --------------------------------------------------------------

function isReducedMotionPref(v: unknown): v is ReducedMotionPref {
  return v === 'auto' || v === 'on' || v === 'off';
}

/** Loads settings, coalescing each missing/invalid field to its default. Never throws. */
export function loadSettings(): Settings {
  const raw = readRaw(KEY_SETTINGS);
  if (raw === null) {
    return DEFAULT_SETTINGS;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return DEFAULT_SETTINGS;
    }
    const obj = parsed as Record<string, unknown>;
    return {
      sound: typeof obj['sound'] === 'boolean' ? obj['sound'] : DEFAULT_SETTINGS.sound,
      symbols: typeof obj['symbols'] === 'boolean' ? obj['symbols'] : DEFAULT_SETTINGS.symbols,
      reducedMotion: isReducedMotionPref(obj['reducedMotion'])
        ? obj['reducedMotion']
        : DEFAULT_SETTINGS.reducedMotion,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(settings: Settings): void {
  writeRaw(KEY_SETTINGS, JSON.stringify(settings));
}

// ---- progress (stars / completed per level, §7) ----------------------------

function isLevelRecord(v: unknown): v is LevelRecord {
  if (v === null || typeof v !== 'object') {
    return false;
  }
  const o = v as Record<string, unknown>;
  return typeof o['completed'] === 'boolean' && typeof o['stars'] === 'number';
}

/** Loads the progress map. Returns an empty object when absent/corrupt. Never throws. */
export function loadProgress(): ProgressMap {
  const raw = readRaw(KEY_PROGRESS);
  if (raw === null) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return {};
    }
    const out: Record<string, LevelRecord> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (isLevelRecord(value)) {
        out[key] = { completed: value.completed, stars: value.stars };
      }
    }
    return out;
  } catch {
    return {};
  }
}

function saveProgress(progress: ProgressMap): void {
  writeRaw(KEY_PROGRESS, JSON.stringify(progress));
}

/** Stars earned for `level` (0 if never recorded). */
export function getLevelStars(level: number): number {
  const rec = loadProgress()[String(level)];
  return rec === undefined ? 0 : rec.stars;
}

/** True iff `level` has been completed at least once. */
export function isLevelCompleted(level: number): boolean {
  const rec = loadProgress()[String(level)];
  return rec !== undefined && rec.completed;
}

/**
 * Records a completion. Keeps the BEST star count ever achieved for the level
 * (re-clearing with fewer stars never downgrades a previously earned star).
 */
export function recordLevelResult(level: number, stars: number): void {
  const key = String(level);
  const progress = { ...loadProgress() };
  const prior = progress[key];
  const bestStars = Math.max(prior === undefined ? 0 : prior.stars, stars);
  progress[key] = { completed: true, stars: bestStars };
  saveProgress(progress);
}

/**
 * Wipes persisted game PROGRESS (the progress map + resume level) so play restarts from Level 1
 * (Settings → Reset progress). Settings and the first-run tutorial flag are intentionally preserved.
 */
export function resetAllProgress(): void {
  removeRaw(KEY_PROGRESS);
  removeRaw(KEY_CURRENT_LEVEL);
}

// ---- durability ------------------------------------------------------------

/**
 * Best-effort: ask the browser to make this origin's storage DURABLE so saved progress isn't evicted
 * under storage pressure. The browser may grant or deny; either way this never throws. Call once on
 * launch. (Installed PWAs are typically granted persistence automatically.)
 */
export function requestPersistentStorage(): void {
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.storage !== undefined &&
      typeof navigator.storage.persist === 'function'
    ) {
      void navigator.storage.persist().catch(() => undefined);
    }
  } catch {
    // durability is a hint, not a requirement — ignore any failure
  }
}
